import { useState } from 'react'
import { View, Text } from 'react-native'
import { BrandedRefreshControl } from '@/components/ui/BrandedRefreshControl'
import { ScrollView } from 'react-native-gesture-handler'
import { useRouter } from 'expo-router'
import { useTranslation } from 'react-i18next'
import { useScreenInsets } from '@/lib/hooks/use-screen-insets'
import Animated from 'react-native-reanimated'
import { Car, ChevronRight, Calendar } from '@/components/ui/icons'
import { Image as ExpoImage } from 'expo-image'
import { toPlaceholder, GENERIC_IMAGE_BLURHASH } from '@/lib/utils/blurhash'
import { Pressable } from '@/components/ui/Pressable'
import { SegmentedControl } from '@/components/ui/SegmentedControl'
import { EmptyState } from '@/components/ui/EmptyState'
import { Skeleton } from '@/components/ui/Skeleton'
import { ScreenContainer } from '@/components/ui/ScreenContainer'
import { useUserBookings } from '@/lib/hooks/use-booking'
import { useSemanticColors, SPACING, RADIUS, TEXT_STYLES } from '@/lib/theme/tokens'
import { COLORS, FONTS } from '@/lib/brand'
import { WEB_SHADOWS } from '@/lib/theme/web-shadow'
import { useTheme } from '@/lib/hooks/use-theme'
import { useEntrance } from '@/lib/hooks/use-entrance'
import { useBreakpoint } from '@/lib/hooks/use-breakpoint'
import { PageShell } from '@/components/layout/PageShell'
import { formatBookingDate } from '@/lib/utils/dates'
import { formatPrice } from '@/lib/utils/format'
import type { BookingListFilter } from '@/lib/types/booking.types'

/** Height of one trip row. The skeleton stands in for exactly this. */
const TRIP_ROW_HEIGHT = 88

const FILTERS: Array<{ value: BookingListFilter; label: string }> = [
  { value: 'upcoming', label: 'trips.upcoming' },
  { value: 'past', label: 'trips.past' },
  { value: 'all', label: 'trips.all' },
]

export default function TripsScreen() {
  const { t, i18n } = useTranslation()
  const router = useRouter()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const insets = useScreenInsets()
  const { isDesktop: isWide } = useBreakpoint()
  const [filter, setFilter] = useState<BookingListFilter>('upcoming')
  const { bookings, isLoading, error, isRefetching, refetch } = useUserBookings('guest', filter)
  const enter = useEntrance(!isLoading)

  const formatDate = (iso: string) => {
    return formatBookingDate(iso, i18n.language, { month: 'short', day: 'numeric' })
  }

  return (
    <ScreenContainer variant="reading" scroll={false}>
      <PageShell title={t('trips.title')}>
        {/* Mobile-only title row — on desktop, PageShell owns the h1.
            No `insets.top` here: ScreenContainer already pads the safe
            area, and adding it twice left a status-bar-tall dead band
            above the title. */}
        {!isWide && (
          <View
            style={{
              paddingHorizontal: SPACING.lg,
              paddingTop: SPACING.sm,
              paddingBottom: SPACING.md,
            }}
          >
            <Text
              style={{
                ...TEXT_STYLES.h1,
                fontFamily: FONTS.bodyBold,
                color: colors.textPrimary,
              }}
            >
              {t('trips.title')}
            </Text>
          </View>
        )}

        {/* Filter tabs */}
        <View
          style={{
            paddingHorizontal: isWide ? 0 : SPACING.lg,
            marginBottom: SPACING.md,
          }}
        >
          <SegmentedControl
            options={FILTERS.map((f) => ({ value: f.value, label: t(f.label) }))}
            value={filter}
            onChange={(v) => setFilter(v as BookingListFilter)}
            testIDPrefix="trips.filter"
          />
        </View>

        <ScrollView
          contentContainerStyle={{
            // `flexGrow` so the states that have no list — empty and error —
            // can centre themselves in the whole area instead of hugging the
            // segmented control with a void twice their size underneath.
            flexGrow: 1,
            paddingHorizontal: isWide ? 0 : SPACING.lg,
            paddingBottom: insets.bottom + SPACING.xl,
            // PageShell already caps width + centers on desktop, so the
            // ScrollView just fills the frame.
          }}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
          refreshControl={
            <BrandedRefreshControl refreshing={isRefetching} onRefresh={() => void refetch()} />
          }
        >
          {isLoading ? (
            // Same gap and same row height as the list below, so the swap
            // from skeleton to trips moves nothing.
            <View style={{ gap: isWide ? SPACING.md : SPACING.sm }}>
              <Skeleton
                width="100%"
                height={TRIP_ROW_HEIGHT}
                borderRadius={RADIUS.lg}
                staggerIndex={0}
              />
              <Skeleton
                width="100%"
                height={TRIP_ROW_HEIGHT}
                borderRadius={RADIUS.lg}
                staggerIndex={1}
              />
              <Skeleton
                width="100%"
                height={TRIP_ROW_HEIGHT}
                borderRadius={RADIUS.lg}
                staggerIndex={2}
              />
            </View>
          ) : error ? (
            // A failed request is not an empty list. This used to render
            // "Sin viajes" to someone whose trips simply had not loaded —
            // telling them their bookings do not exist, with nothing to tap.
            <View style={{ flex: 1, justifyContent: 'center' }}>
              <EmptyState
                variant="aurora"
                title={t('common.error_title')}
                description={t('common.error_description')}
                action={{ label: t('common.retry'), onPress: () => void refetch() }}
              />
            </View>
          ) : bookings.length === 0 ? (
            // Not a dead end: the Past filter says something true about the
            // past, and every variant hands the user the way out (Explore).
            <View style={{ flex: 1, justifyContent: 'center' }}>
              <EmptyState
                variant="bloom"
                illustration={<Car size={36} color={COLORS.coral.DEFAULT} strokeWidth={1.6} />}
                title={filter === 'past' ? t('trips.no_past_trips') : t('trips.no_trips')}
                description={
                  filter === 'past' ? t('trips.no_past_trips_desc') : t('trips.no_trips_desc')
                }
                action={{
                  label: t('explore.search_cta'),
                  onPress: () => router.push('/(tabs)/explore'),
                }}
              />
            </View>
          ) : (
            // Single-column list. On desktop the reading-width column
            // (720px) keeps the cards comfortably wide without the
            // awkward 2-column wrap at mixed viewport sizes. Messages
            // tab uses the same column so the two tabs read as siblings.
            <View testID="trips.trip-list" style={{ gap: isWide ? SPACING.md : SPACING.sm }}>
              {bookings.map((booking, index) => (
                <Animated.View
                  key={booking.id}
                  testID={`trips.item-${index}`}
                  collapsable={false}
                  entering={enter(index)}
                >
                  <Pressable
                    onPress={() => router.push(`/trips/${booking.id}`)}
                    accessibilityRole="button"
                    accessibilityLabel={booking.vehicleTitle}
                    testID={`trips.trip-${index}`}
                    style={{
                      flexDirection: 'row',
                      backgroundColor: colors.surface,
                      borderRadius: RADIUS.lg,
                      overflow: 'hidden',
                      ...WEB_SHADOWS.sm,
                    }}
                  >
                    <View
                      style={{
                        width: 100,
                        height: TRIP_ROW_HEIGHT,
                        backgroundColor: colors.surfaceSubtle,
                      }}
                    >
                      {booking.vehiclePrimaryImageUrl ? (
                        <ExpoImage
                          source={booking.vehiclePrimaryImageUrl}
                          placeholder={toPlaceholder(
                            booking.vehiclePrimaryImageBlurhash ?? GENERIC_IMAGE_BLURHASH,
                          )}
                          style={{ width: '100%', height: '100%' }}
                          contentFit="cover"
                        />
                      ) : (
                        <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
                          <Car size={28} color={colors.textMuted} strokeWidth={1.3} />
                        </View>
                      )}
                    </View>
                    <View style={{ flex: 1, padding: SPACING.sm, justifyContent: 'center' }}>
                      <Text
                        style={{
                          fontSize: 15,
                          fontFamily: FONTS.bodyBold,
                          color: colors.textPrimary,
                        }}
                        numberOfLines={1}
                      >
                        {booking.vehicleTitle}
                      </Text>
                      <View
                        style={{
                          flexDirection: 'row',
                          alignItems: 'center',
                          marginTop: SPACING.xs,
                          gap: SPACING.xs,
                        }}
                      >
                        <Calendar size={14} color={colors.textSecondary} strokeWidth={1.8} />
                        <Text
                          style={{
                            fontSize: 13,
                            fontFamily: FONTS.body,
                            color: colors.textSecondary,
                          }}
                        >
                          {formatDate(booking.startAt)} → {formatDate(booking.endAt)}
                        </Text>
                      </View>
                      <View
                        style={{
                          flexDirection: 'row',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          marginTop: SPACING.xs,
                          gap: SPACING.sm,
                        }}
                      >
                        <Text
                          style={{
                            fontSize: 14,
                            fontFamily: FONTS.bodyBold,
                            color: colors.textPrimary,
                          }}
                        >
                          {formatPrice(booking.totalCents)}
                        </Text>
                        <TripStatusBadge status={booking.status} />
                      </View>
                    </View>
                    <View style={{ justifyContent: 'center', paddingRight: SPACING.sm }}>
                      <ChevronRight size={18} color={colors.textMuted} strokeWidth={2} />
                    </View>
                  </Pressable>
                </Animated.View>
              ))}
            </View>
          )}
        </ScrollView>
      </PageShell>
    </ScreenContainer>
  )
}

/**
 * Status pill — surfaces rental state (pending payment, confirmed, active…)
 * on the trip card. Payment-pending uses coral to draw the eye; an at-risk
 * booking that the user abandoned mid-checkout shows up clearly instead of
 * quietly blending in with confirmed trips.
 */
function TripStatusBadge({ status }: { status: string }) {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)

  const tone = (() => {
    switch (status) {
      case 'pending_payment':
        return { bg: `${COLORS.coral.DEFAULT}1A`, fg: COLORS.coral.DEFAULT }
      case 'awaiting_host':
        return { bg: `${COLORS.amber.DEFAULT}22`, fg: COLORS.amber.dark }
      case 'confirmed':
      case 'pending_pickup':
        return { bg: `${COLORS.amber.DEFAULT}22`, fg: COLORS.amber.dark }
      case 'active':
      case 'pending_return':
        return { bg: `${colors.success}1A`, fg: colors.success }
      case 'completed':
        return { bg: colors.surfaceSubtle, fg: colors.textSecondary }
      case 'cancelled':
      case 'disputed':
        return { bg: colors.surfaceSubtle, fg: colors.textMuted }
      default:
        return { bg: colors.surfaceSubtle, fg: colors.textSecondary }
    }
  })()

  return (
    <View
      style={{
        paddingHorizontal: SPACING.sm,
        paddingVertical: 4,
        borderRadius: RADIUS.full,
        backgroundColor: tone.bg,
      }}
    >
      <Text style={{ fontSize: 12, fontFamily: FONTS.body, color: tone.fg }} numberOfLines={1}>
        {t(`hosting.booking_status_${status}`)}
      </Text>
    </View>
  )
}
